import { Menu, MenuItem, Button, Box } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useRef, useState } from "react";
import { ModalState } from "../../../pages/v2/UsersPage";
import DownIcon from "@mui/icons-material/KeyboardArrowDown";
import RightIcon from "@mui/icons-material/KeyboardArrowRight";
import { AlertState } from "../../../types";
import { DbUser } from "../../../interfaces";
import { AddClass } from "./AddDetails/AddClass";
import { AddSchool } from "./AddDetails/AddSchool";
import { AddBulkUsers } from "./AddDetails/AddBulkUsers";
import { generateGtmEvent } from "../../../Helpers";
import { AddLtiTool } from "./AddDetails/AddLtiTool";
import { AddMaintenanceBanner } from "./AddDetails/AddMaintenanceBanner";
import { EditUserV2 } from "./EditDetails/EditUserV2";
import { AddCourse } from "./AddDetails/AddCourse";
import { DEV_AND_LEAD_EMAILS } from "../../../constants";

const useStyles = makeStyles((theme) => ({
  menuPaper: {
    boxShadow: "0px 8px 12px #00000019",
    border: "1px solid #E8E8E8",
    borderRadius: 8,
  },
  menuList: {
    width: 193,
    padding: "12px",
    "& li": {
      color: "#747474",
      fontWeight: "500",
      padding: "8px 12px",
    },
  },
}));

type AddItemButtonProps = {
  setModalState: (newState: ModalState | null) => void;
  modalState: ModalState | null;
  currentUser: DbUser;
  handleSubmit: (alert: AlertState) => void;
  // Added because data layer requires this to be passed
  sectionName: string;
};

export function AddNewItemBtn(props: AddItemButtonProps) {
  const classes = useStyles();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const userButtonRef = useRef<HTMLLIElement | null>(null);

  const [mainMenuOpen, setMainMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleClassModalClose = () => {
    setMainMenuOpen(false);
    props.setModalState(null);
  };
  const handleSingleUserModalClose = () => {
    setMainMenuOpen(false);
    setUserMenuOpen(false);
    props.setModalState(null);
  };
  const handleBulkUserModalClose = () => {
    setMainMenuOpen(false);
    setUserMenuOpen(false);
    props.setModalState(null);
  };
  const handleSchoolModalClose = () => {
    props.setModalState(null);
  };
  const handleCourseModalClose = () => {
    setMainMenuOpen(false);
    props.setModalState(null);
  };

  const handleMaintenanceBannerModalClose = () => {
    props.setModalState(null);
  };

  const HandleDataLayer = (section: string) => {
    generateGtmEvent({
      event: "collapse",
      action: "open",
      name: "onclick",
      type: "click",
      region: "main content",
      section: section,
      text: "Add",
    });
  };

  return (
    <Box>
      <Button
        disableRipple={true}
        sx={{ padding: "12px 16px" }}
        variant="contained"
        size="large"
        disableElevation
        ref={buttonRef}
        onClick={() => {
          HandleDataLayer(props.sectionName);
          setMainMenuOpen(true);
        }}>
        Add
        <DownIcon
          sx={[
            {
              marginLeft: "12px",
              transition: (theme) => theme.transitions.create("transform"),
            },
            mainMenuOpen && { transform: "rotate(180deg)" },
          ]}
        />
      </Button>
      <Menu
        anchorEl={buttonRef.current}
        open={mainMenuOpen}
        onClose={() => {
          generateGtmEvent({
            event: "collapse",
            action: "close",
            name: "onclick",
            type: "click",
            region: "main content",
            section: props.sectionName,
            text: "add",
          });
          setMainMenuOpen(false);
        }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: -8 }}
        MenuListProps={{ className: classes.menuList }}
        PaperProps={{ className: classes.menuPaper }}>
        <MenuItem ref={userButtonRef} onClick={() => setUserMenuOpen(true)}>
          User
          <RightIcon sx={{ marginLeft: "100px" }} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            generateGtmEvent({
              event: "modal",
              action: "open",
              name: "onclick",
              type: "click",
              region: "main content",
              section: "add ^ class",
              text: "class",
            });
            props.setModalState("class");
          }}>
          Class
        </MenuItem>
        {props.currentUser?.role === "SUPER" && (
          <MenuItem
            onClick={() => {
              generateGtmEvent({
                event: "modal",
                action: "open",
                name: "onclick",
                type: "click",
                region: "main content",
                section: "add ^ school",
                text: "school",
              });
              props.setModalState("school");
            }}>
            School
          </MenuItem>
        )}
        {props.currentUser?.role === "SUPER" &&
          DEV_AND_LEAD_EMAILS.includes(props.currentUser?.email) && (
            <MenuItem
              onClick={() => {
                generateGtmEvent({
                  event: "modal",
                  action: "open",
                  name: "onclick",
                  type: "click",
                  region: "main content",
                  section: "add ^ lti tool",
                  text: "lti tool",
                });
                props.setModalState("ltitool");
              }}>
              Lti Tool
            </MenuItem>
          )}
        {props.currentUser?.role === "SUPER" && (
          <MenuItem
            onClick={() => {
              generateGtmEvent({
                event: "modal",
                action: "open",
                name: "onclick",
                type: "click",
                region: "main content",
                section: "add ^ school",
                text: "school",
              });
              props.setModalState("course");
            }}>
            Course
          </MenuItem>
        )}
        {props.currentUser?.role === "SUPER" && (
          <MenuItem
            onClick={() => {
              generateGtmEvent({
                event: "modal",
                action: "open",
                name: "onclick",
                type: "click",
                region: "main content",
                section: "add ^ maintenance banner",
                text: "maintenance banner",
              });
              props.setModalState("maintenanceBanner");
            }}>
            Maintenance Banner
          </MenuItem>
        )}
      </Menu>
      <Menu
        anchorEl={userButtonRef.current}
        open={userMenuOpen}
        onClose={() => setUserMenuOpen(false)}
        anchorOrigin={{ horizontal: "left", vertical: -12 }}
        transformOrigin={{ horizontal: 204, vertical: 0 }}
        MenuListProps={{ className: classes.menuList }}
        PaperProps={{ className: classes.menuPaper }}
        sx={{ marginTop: "0px" }}>
        <MenuItem
          onClick={() => {
            generateGtmEvent({
              event: "modal",
              action: "open",
              name: "onclick",
              type: "click",
              region: "main content",
              section: "add ^ user",
              text: "single user",
            });
            props.setModalState("single");
          }}>
          Single User
        </MenuItem>
        <MenuItem
          onClick={() => {
            generateGtmEvent({
              event: "modal",
              action: "open",
              name: "onclick",
              type: "click",
              region: "main content",
              section: "add ^ user",
              text: "bulk upload",
            });
            props.setModalState("bulk");
          }}>
          Bulk Upload
        </MenuItem>
      </Menu>

      <EditUserV2
        open={props.modalState === "single"}
        onClose={handleSingleUserModalClose}
        currentUser={props.currentUser}
        onSubmit={props.handleSubmit}
      />
      <AddClass
        open={props.modalState === "class"}
        onClose={handleClassModalClose}
        currentUser={props.currentUser}
        onSubmit={props.handleSubmit}
      />
      <AddSchool
        open={props.modalState === "school"}
        onClose={handleSchoolModalClose}
        currentUser={props.currentUser}
        onSubmit={props.handleSubmit}
      />
      <AddMaintenanceBanner
        open={props.modalState === "maintenanceBanner"}
        onClose={handleMaintenanceBannerModalClose}
        currentUser={props.currentUser}
        onSubmit={props.handleSubmit}
      />
      <AddCourse
        open={props.modalState === "course"}
        onClose={handleCourseModalClose}
        currentUser={props.currentUser}
        onSubmit={props.handleSubmit}
      />
      <AddBulkUsers
        open={props.modalState === "bulk"}
        onClose={handleBulkUserModalClose}
        onSubmit={props.handleSubmit}
        currentUser={props.currentUser}
      />
      <AddLtiTool
        open={props.modalState === "ltitool"}
        onClose={() => {
          props.setModalState(null);
        }}
      />
    </Box>
  );
}
